import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { useI18n } from '../i18n'
import DashboardLayout from '../components/DashboardLayout'
import { parseEmails, createInvitations, sendInviteEmails } from '../lib/invites'
import { nickSuffix } from '../lib/nick'

// ─── Helpers ───────────────────────────────────────────────────

const daysLeft = (endsAt) => {
  if (!endsAt) return null
  const diff = new Date(endsAt) - new Date()
  return Math.max(0, Math.floor(diff / 86400000))
}

// ─── Sub-components ────────────────────────────────────────────

const TrustCircleCard = ({ member, t }) => {
  const hex = member.member?.hex_code || '#888888'
  const r = parseInt(hex.slice(1,3),16)
  const g = parseInt(hex.slice(3,5),16)
  const b = parseInt(hex.slice(5,7),16)
  return (
    <div style={{
      background: `rgba(${r},${g},${b},0.08)`,
      border: `1px solid rgba(${r},${g},${b},0.20)`,
      borderRadius: 16, padding: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: hex, flexShrink: 0 }} />
        <span className="mono">{hex}{nickSuffix(member.member?.nickname)}</span>
      </div>
      {member.gift_title && (
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: 'var(--text-muted)', margin: 0 }}>
          {t.trustTeam.gaveGift}
          <br />
          <em>"{member.gift_title}"</em>
        </p>
      )}
    </div>
  )
}

const InvitedRow = ({ inv, t, onEnd, onCopy, onResend, flash, communityName }) => {
  const days = daysLeft(inv.subscription_ends_at)
  const ended = inv.subscription_ends_at && days === 0
  const pending = inv.status === 'pending'

  let statusLabel = t.trustTeam.subscriptionActive
  let statusColor = 'rgba(80,200,120,0.8)'
  if (pending) { statusLabel = t.trustTeam.subscriptionPending; statusColor = 'var(--text-muted)' }
  if (ended) { statusLabel = t.trustTeam.subscriptionEnded; statusColor = '#e05c5c' }

  return (
    <div style={{
      background: 'var(--surface)', borderRadius: 14, padding: '16px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      border: '1px solid var(--border)',
    }}>
      <div>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: 'var(--text-primary)', margin: '0 0 4px' }}>
          {inv.email}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: statusColor }}>
            {statusLabel}
          </span>
          {!ended && !pending && days !== null && (
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'var(--text-muted)' }}>
              · {t.trustTeam.daysLeft(days)}
            </span>
          )}
          {communityName && (
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'var(--text-muted)' }}>
              · {t.trustTeam.inCommunity}: {communityName}
            </span>
          )}
        </div>
      </div>
      {pending && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {flash && <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'rgba(80,200,120,0.8)' }}>{flash}</span>}
          <button className="btn-ghost" onClick={() => onCopy(inv.id)} style={{ fontSize: 14, padding: '4px 10px' }}>{t.trustTeam.copyLinkShort}</button>
          <button className="btn-ghost" onClick={() => onResend(inv.id)} style={{ fontSize: 14, padding: '4px 10px' }}>{t.trustTeam.resend}</button>
        </div>
      )}
      {!ended && !pending && (
        <button
          className="btn-ghost"
          onClick={() => onEnd(inv.id)}
          style={{ fontSize: 14, padding: '4px 10px' }}
        >
          {t.trustTeam.endSubscription}
        </button>
      )}
    </div>
  )
}

// ─── Invite Modal ──────────────────────────────────────────────

const InviteModal = ({ onClose, onSuccess, user, t }) => {
  const m = t.trustTeam.modal
  const [step, setStep] = useState(1)
  const [emailText, setEmailText] = useState('')
  const [duration, setDuration] = useState(12)
  const [communityChoice, setCommunityChoice] = useState('new')
  const [communityName, setCommunityName] = useState('')
  const [selectedCommunityId, setSelectedCommunityId] = useState('')
  const [communities, setCommunities] = useState([])
  const [results, setResults] = useState([])
  const [mailSent, setMailSent] = useState(true)
  const [copiedAll, setCopiedAll] = useState(false)
  const [loading, setLoading] = useState(false)

  const { valid, invalid } = parseEmails(emailText)
  const targets = valid.filter((e) => e !== (user.email || '').toLowerCase())

  useEffect(() => {
    supabase.from('sub_communities').select('*').eq('owner_id', user.id)
      .then(({ data }) => {
        setCommunities(data || [])
        if (data && data.length > 0) {
          setCommunityChoice('existing')
          setSelectedCommunityId(data[0].id)
        }
      })
  }, [user.id])

  const handleGenerate = async () => {
    setLoading(true)
    try {
      let communityId = null
      if (communityChoice === 'new' && communityName.trim()) {
        const { data: comm, error: commErr } = await supabase
          .from('sub_communities')
          .insert({ owner_id: user.id, name: communityName.trim(), members_aware: true })
          .select().single()
        if (commErr) throw commErr
        communityId = comm.id
        await supabase
          .from('sub_community_members')
          .upsert({ sub_community_id: comm.id, user_id: user.id }, { onConflict: 'sub_community_id,user_id' })
      } else if (communityChoice === 'existing' && selectedCommunityId) {
        communityId = selectedCommunityId
      }

      const created = await createInvitations({ userId: user.id, emails: targets, months: duration, communityId })
      const ids = created.filter((r) => r.id).map((r) => r.id)
      setMailSent(await sendInviteEmails(ids))
      setResults(created)
      setStep(4)
      onSuccess()
    } catch (err) {
      console.error('handleGenerate error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCopyAll = () => {
    const txt = results.filter((r) => r.link).map((r) => `${r.email}\t${r.link}`).join('\n')
    navigator.clipboard.writeText(txt)
    setCopiedAll(true)
    setTimeout(() => setCopiedAll(false), 2000)
  }

  const s = { fontFamily: "'DM Sans', sans-serif" }
  const stepLabel = { ...s, fontSize: 14, color: 'var(--text-muted)', marginBottom: 4 }
  const title = { ...s, fontSize: 20, fontWeight: 500, marginBottom: 24, color: 'var(--text-primary)' }

  const DurationBtn = ({ months, label }) => (
    <button
      onClick={() => setDuration(months)}
      style={{
        flex: 1, padding: '12px 8px', borderRadius: 10, cursor: 'pointer',
        fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 500,
        background: duration === months ? 'var(--user-color)' : 'var(--surface-2)',
        color: duration === months ? 'var(--user-color-text)' : 'var(--text-muted)',
        border: duration === months ? '1px solid var(--user-color)' : '1px solid var(--border)',
        transition: 'all 150ms ease',
      }}
    >
      {label}
    </button>
  )

  const CommunityOption = ({ value, label }) => (
    <button
      onClick={() => setCommunityChoice(value)}
      style={{
        width: '100%', padding: '12px 16px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
        fontFamily: "'DM Sans', sans-serif", fontSize: 16,
        background: communityChoice === value ? 'var(--user-color-soft)' : 'var(--surface-2)',
        color: communityChoice === value ? 'var(--text-primary)' : 'var(--text-muted)',
        border: communityChoice === value ? '1px solid var(--user-color)' : '1px solid var(--border)',
        marginBottom: 8, transition: 'all 150ms ease',
      }}
    >
      {label}
    </button>
  )

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: 20, padding: 32, maxWidth: 440, width: '100%', border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? 'var(--user-color)' : 'var(--border)', transition: 'background 300ms ease' }} />
          ))}
        </div>

        {step === 1 && (
          <>
            <p style={stepLabel}>{m.step1}</p>
            <h2 style={title}>{m.title}</h2>
            <label style={{ ...s, fontSize: 15, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>{m.bulkLabel}</label>
            <textarea
              value={emailText}
              onChange={(e) => setEmailText(e.target.value)}
              placeholder={m.bulkPlaceholder}
              style={{ minHeight: 140, fontFamily: "'DM Mono', monospace", fontSize: 15 }}
            />
            <p style={{ ...s, fontSize: 14, color: 'var(--text-muted)', marginTop: 6 }}>{m.bulkHint(targets.length)}</p>
            {invalid.length > 0 && (
              <p style={{ ...s, fontSize: 14, color: '#e05c5c', marginTop: 4 }}>{invalid.join(', ')}</p>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
              <button className="btn-secondary" onClick={onClose} style={{ flex: 1 }}>{m.close}</button>
              <button className="btn-primary" onClick={() => setStep(2)} disabled={targets.length === 0} style={{ flex: 1 }}>{m.next}</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p style={stepLabel}>{m.step2}</p>
            <h2 style={title}>{m.bulkSubject(targets.length)}</h2>
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              <DurationBtn months={6} label={m.duration6} />
              <DurationBtn months={12} label={m.duration12} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-secondary" onClick={() => setStep(1)} style={{ flex: 1 }}>{m.back}</button>
              <button className="btn-primary" onClick={() => setStep(3)} style={{ flex: 1 }}>{m.next}</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <p style={stepLabel}>{m.step3}</p>
            <h2 style={title}>{m.bulkSubject(targets.length)}</h2>

            {communities.length > 0 && <CommunityOption value="existing" label={m.communityExisting} />}
            <CommunityOption value="new" label={m.communityNew} />

            {communityChoice === 'new' && (
              <div style={{ marginTop: 12, padding: 16, background: 'var(--surface-2)', borderRadius: 12 }}>
                <label style={{ ...s, fontSize: 15, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>{m.communityNameLabel}</label>
                <input type="text" value={communityName} onChange={(e) => setCommunityName(e.target.value)} placeholder="e.g. Core Group" />
              </div>
            )}

            {communityChoice === 'existing' && communities.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <select value={selectedCommunityId} onChange={(e) => setSelectedCommunityId(e.target.value)}>
                  {communities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button className="btn-secondary" onClick={() => setStep(2)} style={{ flex: 1 }}>{m.back}</button>
              <button
                className="btn-primary"
                onClick={handleGenerate}
                disabled={loading || (communityChoice === 'new' && !communityName.trim()) || (communityChoice === 'existing' && !selectedCommunityId)}
                style={{ flex: 1 }}
              >
                {loading ? '…' : m.generate}
              </button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <p style={stepLabel}>{m.step4}</p>
            <h2 style={title}>{m.bulkReady(results.filter((r) => r.id).length)}</h2>
            <p style={{ ...s, fontSize: 15, color: 'var(--text-muted)', marginBottom: 16 }}>
              {mailSent ? m.sentNote : t.community.mailNote}
            </p>
            <div style={{ maxHeight: 240, overflowY: 'auto', background: 'var(--surface-2)', borderRadius: 12, padding: 12, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {results.map((r, i) => (
                <div key={i} style={{ padding: '8px 10px', borderRadius: 8, background: r.error ? 'rgba(224,92,92,0.10)' : 'var(--surface)' }}>
                  <p style={{ ...s, fontSize: 14, color: 'var(--text-muted)', margin: '0 0 2px' }}>
                    {r.email}{r.duplicate ? ` · ${t.trustTeam.alreadyInvited}` : ''}
                  </p>
                  <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, margin: 0, wordBreak: 'break-all', color: r.error ? '#e05c5c' : 'var(--text-primary)' }}>
                    {r.error || r.link}
                  </p>
                </div>
              ))}
            </div>
            <button className="btn-primary" onClick={handleCopyAll} style={{ width: '100%', marginBottom: 12 }}>
              {copiedAll ? m.copied : m.copyAll}
            </button>
            <button className="btn-secondary" onClick={onClose} style={{ width: '100%' }}>{m.close}</button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────

const GuvenTakimi = () => {
  const { user } = useAuth()
  const { t } = useI18n()
  const [activeTab, setActiveTab] = useState('circle')
  const [trustCircle, setTrustCircle] = useState([])
  const [invited, setInvited] = useState([])
  const [communityNames, setCommunityNames] = useState({})
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)

  const fetchTrustCircle = useCallback(async () => {
    if (!user) return
    const { data } = await supabase
      .from('trust_circle')
      .select('*, member:member_id(*)')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: false })
    setTrustCircle(data || [])
  }, [user])

  const fetchInvited = useCallback(async () => {
    if (!user) return
    const { data } = await supabase
      .from('invitations')
      .select('*')
      .eq('inviter_id', user.id)
      .order('created_at', { ascending: false })
    setInvited(data || [])
    const { data: comms } = await supabase.from('sub_communities').select('id, name')
    setCommunityNames(Object.fromEntries((comms || []).map((c) => [c.id, c.name])))
  }, [user])

  useEffect(() => {
    if (!user) return
    setLoading(true)
    Promise.all([fetchTrustCircle(), fetchInvited()]).finally(() => setLoading(false))
  }, [user, fetchTrustCircle, fetchInvited])

  const [flashId, setFlashId] = useState(null)
  const [flashText, setFlashText] = useState('')
  const flash = (id, text) => {
    setFlashId(id); setFlashText(text)
    setTimeout(() => setFlashId(null), 2000)
  }
  const handleCopyInvite = (id) => {
    navigator.clipboard.writeText(`${window.location.origin}/davet/${id}`)
    flash(id, t.trustTeam.modal.copied)
  }
  const handleResendInvite = async (id) => {
    const ok = await sendInviteEmails([id], false)
    flash(id, ok ? t.trustTeam.sentShort : t.trustTeam.sendFailed)
  }

  const handleEndSubscription = async (invitationId) => {
    await supabase
      .from('invitations')
      .update({ subscription_ends_at: new Date().toISOString() })
      .eq('id', invitationId)
    fetchInvited()
  }

  const tabStyle = (active) => ({
    fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 500,
    color: active ? 'var(--text-primary)' : 'var(--text-muted)',
    background: 'none', border: 'none',
    borderBottom: active ? '2px solid var(--user-color)' : '2px solid transparent',
    padding: '12px 0', cursor: 'pointer', marginRight: 24, transition: 'color 150ms ease',
  })

  return (
    <DashboardLayout>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 32, fontWeight: 500, marginBottom: 8 }}>{t.trustTeam.title}</h1>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: 'var(--text-muted)', margin: 0 }}>{t.trustTeam.subtitle}</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)} style={{ flexShrink: 0 }}>
          {t.trustTeam.invite}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--border)', marginBottom: 32 }}>
        <button style={tabStyle(activeTab === 'circle')} onClick={() => setActiveTab('circle')}>
          {t.trustTeam.tabCircle}
          {trustCircle.length > 0 && (
            <span style={{ marginLeft: 8, fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'var(--text-muted)' }}>
              {trustCircle.length}
            </span>
          )}
        </button>
        <button style={tabStyle(activeTab === 'invited')} onClick={() => setActiveTab('invited')}>
          {t.trustTeam.tabInvited}
          {invited.length > 0 && (
            <span style={{ marginLeft: 8, fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'var(--text-muted)' }}>
              {invited.length}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontFamily: "'DM Sans', sans-serif", fontSize: 16 }}>—</p>
      ) : activeTab === 'circle' ? (
        trustCircle.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: 'var(--text-muted)', marginBottom: 8 }}>
              {t.trustTeam.emptyCircle}
            </p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: 'var(--text-muted)', maxWidth: 360, margin: '0 auto' }}>
              {t.trustTeam.emptyCircleHint}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {trustCircle.map(m => <TrustCircleCard key={m.id} member={m} t={t} />)}
          </div>
        )
      ) : (
        invited.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: 'var(--text-muted)' }}>
              {t.trustTeam.emptyInvited}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {invited.map(inv => (
              <InvitedRow key={inv.id} inv={inv} t={t} onEnd={handleEndSubscription}
                onCopy={handleCopyInvite} onResend={handleResendInvite}
                communityName={communityNames[inv.sub_community_id]}
                flash={flashId === inv.id ? flashText : null} />
            ))}
          </div>
        )
      )}

      {showModal && (
        <InviteModal
          user={user}
          t={t}
          onClose={() => setShowModal(false)}
          onSuccess={() => fetchInvited()}
        />
      )}
    </DashboardLayout>
  )
}

export default GuvenTakimi
